import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/common/Layout';
import SprintSyncBanner from './components/common/SprintSyncBanner';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ResetPassword from './components/auth/ResetPassword';
import Signup from './components/auth/Signup';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LandingPage from './components/public/LandingPage';
import ExecutiveSummary from './components/public/ExecutiveSummary';
import SurveyPage from './components/public/SurveyPage';
import WorkstreamIdeaPage from './components/public/WorkstreamIdeaPage';
import Dashboard from './components/dashboard/Dashboard';
import WorkItemsView from './components/workitems/WorkItemsView';
import SprintMetricsView from './components/workitems/SprintMetricsView';
import ReleasesView from './components/releases/ReleasesView';
import ReleaseNotesView from './components/releases/ReleaseNotesView';
import ProductionBugsView from './components/workitems/ProductionBugsView';
import BugTrendsView from './components/workitems/BugTrendsView';
import QualityAssuranceView from './components/quality-assurance/QualityAssuranceView';
import TestEngineeringView from './components/quality-assurance/TestEngineeringView';
import RepositoriesView from './components/quality-assurance/RepositoriesView';
import RecommendedTestsView from './components/quality-assurance/RecommendedTestsView';
import MethodMapsPage from './components/quality-assurance/MethodMapsPage';
import PerformanceView from './components/performance/PerformanceView';
import SecurityPage from './components/security/SecurityPage';
import AdminPage from './components/admin/AdminPage';
import EngineeringProcessesView from './components/engineering-processes/EngineeringProcessesView';
import OpsCenter from './components/ops-center/OpsCenter';
import Compliance from './components/compliance/Compliance';
import MigrationsView from './components/migrations/MigrationsView';
import SHAView from './components/sha/SHAView';
import OperationalExcellenceHub from './components/operational-excellence/OperationalExcellenceHub';
import CustomerBriefingPage from './components/operational-excellence/top-customers/briefing/CustomerBriefingPage';
import AuditConsolePage from './components/operational-excellence/audit-console/AuditConsolePage';
import ConfidencePlanPage from './components/operational-excellence/confidence-plan/ConfidencePlanPage';
import PlatformInvestigationView from './components/platform-investigation/PlatformInvestigationView';
import GuardrailView from './components/guardrail/GuardrailView';
import CoverageLanding from './components/coverage/CoverageLanding';
import CoverageTreeView from './components/coverage/CoverageTreeView';
import ImportManager from './components/import/ImportManager';
import QAHealthDashboard from './components/dashboard/QAHealthDashboard';
import UserProfile from './components/profile/UserProfile';
import AIToolkitView from './components/ai-toolkit/AIToolkitView';
import { isAuthenticated, isAdmin } from './services/authService';

/**
 * Main App component with routing
 * @returns {JSX.Element} App component
 */
const App = () => {
  // Debug logging
  console.log('App component rendering, current path:', window.location.pathname);
  
  return (
    <Router>
      <SprintSyncBanner />
      <Routes>
        {/* Public routes: auth pages and the anonymous survey */}
        <Route 
          path="/login" 
          element={
            isAuthenticated() ? <Navigate to="/dashboard" replace /> : <Login />
          } 
        />
        
        <Route 
          path="/register" 
          element={
            !isAuthenticated() ? <Navigate to="/login" replace /> : !isAdmin() ? <Navigate to="/dashboard" replace /> : <Register />
          } 
        />
        
        <Route
          path="/reset-password"
          element={
            isAuthenticated() ? <Navigate to="/dashboard" replace /> : <ResetPassword />
          }
        />

        <Route
          path="/signup"
          element={
            isAuthenticated() ? <Navigate to="/dashboard" replace /> : <Signup />
          }
        />
        
        {/* Redirect work items route to landing page with work-items section */}
        <Route
          path="/work-items"
          element={<Navigate to="/?section=work-items" replace />}
        />
        
        {/* Public anonymous sprint survey - one URL per stream, no sign-in */}
        <Route
          path="/survey/:stream"
          element={<SurveyPage />}
        />

        {/* Public form to suggest a tech debt / reliability / stability workstream - no sign-in */}
        <Route
          path="/workstream-ideas/new"
          element={<WorkstreamIdeaPage />}
        />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>

          {/* Landing page and dashboards - login required */}
          <Route path="/" element={<LandingPage />} />
          {/* QA Health Dashboard - accessible via LandingPage navigation */}
          <Route 
            path="/qahealth" 
            element={<LandingPage />} 
          />
        
          {/* Standalone QA Health Dashboard - no navigation */}
          <Route 
            path="/qahealth-standalone" 
            element={<QAHealthDashboard />} 
          />
        
          {/* Engineering Processes route */}
          <Route 
            path="/engineering-processes" 
            element={<LandingPage />} 
          />
        
          {/* Application Summary – executive summary, data sources, tech stack */}
          <Route 
            path="/summary" 
            element={<ExecutiveSummary />} 
          />
        
          {/* recommended tests route */}
          <Route
            path="/recommended-tests"
            element={<RecommendedTestsView />}
          />

          {/* method maps page — opened from Daily Regression table */}
          <Route
            path="/daily-regression/method-maps"
            element={<MethodMapsPage />}
          />
        
          {/* repositories route */}
          <Route 
            path="/repositories" 
            element={<RepositoriesView />} 
          />
        
          {/* performance route */}
          <Route 
            path="/performance" 
            element={<PerformanceView />} 
          />
          
          <Route 
            path="/dashboard" 
            element={
              <Layout>
                <Dashboard />
              </Layout>
            } 
          />
          <Route 
            path="/profile" 
            element={
              <Layout>
                <UserProfile />
              </Layout>
            } 
          />
          
          <Route 
            path="/releases" 
            element={
              <Layout>
                <ReleasesView />
              </Layout>
            } 
          />
          
          <Route
            path="/production-bugs"
            element={
              <Layout>
                <ProductionBugsView />
              </Layout>
            }
          />

          <Route
            path="/sprint-metrics"
            element={
              <Layout>
                <SprintMetricsView />
              </Layout>
            }
          />
          
          {/* Internal Bugs moved to public landing page (?section=internal-bugs) */}
          <Route 
            path="/internal-bugs" 
            element={<Navigate to="/?section=internal-bugs" replace />} 
          />
          
          <Route 
            path="/release-notes" 
            element={
              <Layout>
                <ReleaseNotesView />
              </Layout>
            } 
          />
          
          <Route 
            path="/quality-assurance" 
            element={
              <Layout>
                <QualityAssuranceView />
              </Layout>
            } 
          />
          
          <Route
            path="/test-engineering"
            element={
              <Layout>
                <TestEngineeringView />
              </Layout>
            }
          />

          <Route
            path="/ai-toolkit"
            element={
              <Layout>
                <AIToolkitView />
              </Layout>
            }
          />
          
          <Route
            path="/incidents-alerts" 
            element={
              <Layout>
                <OpsCenter />
              </Layout>
            } 
          />
          
          {/* Redirect old ops-center path to new incidents-alerts path */}
          <Route 
            path="/ops-center" 
            element={<Navigate to="/incidents-alerts" replace />} 
          />
          
          <Route 
            path="/migrations" 
            element={
              <Layout>
                <MigrationsView />
              </Layout>
            } 
          />

          <Route
            path="/sha"
            element={
              <Layout>
                <SHAView />
              </Layout>
            }
          />

          <Route
            path="/service-operations"
            element={<Navigate to="/?section=service-operations" replace />}
          />

          <Route
            path="/operational-excellence"
            element={
              <Layout>
                <OperationalExcellenceHub />
              </Layout>
            }
          />

          {/* Leadership briefing: full-page report opened in its own tab from the Top Customers card */}
          <Route path="/briefings/:name" element={<CustomerBriefingPage />} />

          <Route
            path="/audit-console"
            element={
              <Layout>
                <AuditConsolePage />
              </Layout>
            }
          />

          <Route
            path="/confidence-plan"
            element={
              <Layout>
                <ConfidencePlanPage />
              </Layout>
            }
          />

          <Route
            path="/incident-remediation"
            element={<Navigate to="/confidence-plan?tab=incident-remediation" replace />}
          />

          <Route
            path="/platform-investigation"
            element={
              <Layout>
                <div>404 - Page not found</div>
              </Layout>
            }
          />

          <Route
            path="/guardrail"
            element={
              <Layout>
                <GuardrailView />
              </Layout>
            }
          />

          <Route
            path="/coverage"
            element={
              <Layout>
                <CoverageLanding />
              </Layout>
            }
          />
          <Route
            path="/coverage/tree"
            element={
              <Layout>
                <CoverageTreeView />
              </Layout>
            }
          />

          {/* Compliance & SNYK — login required (same areas as Landing "Compliance" section) */}
          <Route 
            path="/compliance" 
            element={<Compliance />} 
          />
          <Route 
            path="/security" 
            element={<SecurityPage />}
          />
          
        </Route>
        
        
        {/* Admin routes */}
        <Route element={<ProtectedRoute requiredPermission="system:admin" />}>
          <Route 
            path="/admin" 
            element={
              <Layout>
                <AdminPage />
              </Layout>
            } 
          />
          
          <Route 
            path="/admin/import" 
            element={
              <Layout>
                <ImportManager />
              </Layout>
            } 
          />
          
          <Route 
            path="/admin/users" 
            element={
              <Layout>
                <div>User Management</div>
              </Layout>
            } 
          />
          
          <Route 
            path="/admin/settings" 
            element={
              <Layout>
                <div>Settings</div>
              </Layout>
            } 
          />
        </Route>
        
        {/* Report routes */}
        <Route element={<ProtectedRoute />}>
          <Route 
            path="/reports/completion" 
            element={
              <Layout>
                <div>Completion Progress Report</div>
              </Layout>
            } 
          />
          
          <Route 
            path="/reports/status" 
            element={
              <Layout>
                <div>Status Distribution Report</div>
              </Layout>
            } 
          />
          
          <Route 
            path="/reports/priority" 
            element={
              <Layout>
                <div>Priority Distribution Report</div>
              </Layout>
            } 
          />
        </Route>
        
        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

export default App;
